import type { MsgKey } from "./i18n";
import { UPLOAD_NAMED_EXTS, spineGroupGaps, spineSlotOf } from "./upload.ts";

/**
 * 喵汪角色的前端模型。
 *
 * **静态资源必填；动画资源非必填但要么不传要么三个后缀齐全**（2026-09-16 改）——
 * 填不齐就存不下。服务端 `admin/pet.ts` 的 checkPetBody 末尾有同样一道，
 * 这里判是为了省掉一趟必然失败的保存，**不是防线**。
 */

/** 一份素材 = 原文件名 + CDN 地址。名字的后缀决定用途，见 spineGroupIssue */
export interface PetAsset {
  name: string;
  url: string;
}

/** 某个编号第一次发布时的身份。服务端算的，只读 */
export interface PublishedIdentity {
  speciesI18n: Record<string, string>;
  quality: number;
  /** 第一次出现在已发布版本里的版本号，报错文案里要说清「哪一版定的」 */
  firstVersion: number;
}

export interface Pet {
  /**
   * 本地行标识，**永不出网**。新建行还没保存时没有服务端身份，
   * 而列表要能渲染、能编辑、能删——拿 petId 当 key 的话新建行全撞在空串上。
   */
  localKey: string;
  /** 编号。运营手填；`null` = 新建草稿还没填 */
  petId: string;
  /** 这一行是否已经在服务端存在。决定保存时走 POST 还是 PUT */
  persisted: boolean;
  /**
   * 这个编号**第一次发布时的身份**；`null` = 没发布过。服务端给的。
   *
   * 发布过之后品种 / 品质 / 启用三项就锁死了，也不许删——玩家的 `user_pet` 存的是
   * 裸 `pet_id`，配置侧删掉或停用它，玩家手里那只当场没有名字也没有素材。
   * 要下线一只请把它在领养池里的权重调成 0。
   *
   * **回整份身份而不是一个布尔**，是为了留出修复路径：一个在别的版本发布之前就已经
   * 把身份改错的草稿，只给布尔的话错值会被锁死、连改回正确值都做不到。
   *
   * ⚠️ **`published` 与 `persisted` 是两件事**：`persisted` 只说「服务端有这一行」，
   * 一个只在草稿里待过的角色也是 persisted，但它可以随便改随便删。
   */
  published: PublishedIdentity | null;
  name: string;
  nameI18n: Record<string, string>;
  species: string;
  speciesI18n: Record<string, string>;
  /** `small` | `medium` | `large` */
  size: string;
  /** `cat` | `dog`，必填 */
  family: string;
  /** 本命 idol，单值；`null` = 非本命 */
  oshiIdolId: string | null;
  color: string;
  quality: number;
  enabled: boolean;
  staticAssets: PetAsset[];
  animAssets: PetAsset[];
}

/** 落库形状。localKey / persisted 是纯前端的，`published` 是服务端算的，都不发出去 */
export type PetBody = Omit<Pet, "localKey" | "persisted" | "published">;

/** 读回来的一行：落库形状 + 服务端算的 `published`。**必填，不给回落** */
export type PetRead = PetBody & { published: PublishedIdentity | null };

/**
 * 品种**不再是枚举**（2026-09-10）。名册里 64 只有 64 个 distinct 品种，一只一个，
 * 值本身也不是分类料（`星空布偶(传说个体)`）——当枚举意味着首发 64 个值 + 128 条 i18n，
 * 以后每加一只都要改代码发一次版。真正的分类是 `family`（猫/狗）。
 *
 * 所以品种是**自由文本 + 多语言**（同 `name`），管端给可搜索下拉，
 * 候选来自本版本已有的品种（`speciesOptions`），允许自由输入。
 */
export const SPECIES_MAX = 32;

/**
 * 品种的扁平值。**前端一律用它，不读 `Pet.species`。**
 *
 * 扁平值是 `species_i18n.en` 的派生值，而派生只发生在 `toServerPet`（落库那一刻）——
 * 新建行填完品种、还没保存时 `Pet.species` 仍是空串。读那个字段的后果实测过：
 * 行内校验报「缺品种」，「完成」按钮永久灰着，这一行根本存不下去。
 */
export const speciesOf = (p: Pick<Pet, "speciesI18n">): string => p.speciesI18n.en?.trim() ?? "";

/**
 * 本版本已经用过的品种，按扁平值去重排序 —— 给品种下拉当候选。
 * 选中一条是**把整份多语言拷过来**，不是只填 en：名册 64 只里同品种会重复出现
 * （同一品种不同颜色），一只一只把五种语言手打一遍必然打错一处。
 */
export const speciesOptions = (all: Pet[]): Array<{ species: string; i18n: Record<string, string> }> => {
  const seen = new Map<string, Record<string, string>>();
  for (const p of all) {
    const k = speciesOf(p);
    if (!k) continue;
    // 同一品种出现多次时留**语言最全的那一份**，不是第一个碰到的：
    // 名册里同品种不同颜色是常事，谁先出现只看 pet_id 排序，
    // first-wins 会让「先出现的那只只填了 en」把别人的日文名整份丢掉且不报错
    const cur = seen.get(k);
    if (!cur || Object.keys(p.speciesI18n).length > Object.keys(cur).length) seen.set(k, p.speciesI18n);
  }
  return [...seen].sort(([a], [b]) => a.localeCompare(b)).map(([species, i18n]) => ({ species, i18n }));
};

/**
 * 拿候选的多语言**补空**，不覆盖已填的。
 *
 * 直接整份替换的话，运营刚打好的日文名会被一个没有日文的候选静默清掉，
 * 而这一步没有确认、也没有撤销。
 */
export const mergeI18n = (cur: Record<string, string>, add: Record<string, string>): Record<string, string> => {
  const out = { ...cur };
  for (const [k, v] of Object.entries(add)) if (!out[k]?.trim() && v.trim()) out[k] = v;
  return out;
};

/** 猫狗大类。取值集合与服务端 admin/pet.ts 逐条一致，由 pet.test.ts 比对 */
export const PET_FAMILIES = ["cat", "dog"] as const;
/** 体型。同上。服务端只存与下发，不参与玩法判断 */
export const PET_SIZES = ["small", "medium", "large"] as const;
/** 取值 → 文案 key。键收窄成联合类型：加一种而忘了补文案就是编译错误，不是空白格 */
export const FAMILY_LABEL: Record<(typeof PET_FAMILIES)[number], MsgKey> = { cat: "pet.family.cat", dog: "pet.family.dog" };
export const SIZE_LABEL: Record<(typeof PET_SIZES)[number], MsgKey> = {
  small: "pet.size.small", medium: "pet.size.medium", large: "pet.size.large",
};

/**
 * 品质档位上限。与服务端 `src/game/pet-quality.ts` 的 PET_QUALITY_MAX 同一份，
 * 由 pet.test.ts 比对。三档（显示名 2026-10-06 起 N/R/SR，之前 R/SR/SSR、再之前四档 N/R/SR/UR；显示名见 adoptPool.ts 的 QUALITY_LABEL）。
 *
 * 这里必须再写一份而不是 import：`web/tsconfig.json` 是 `"include": ["src"]`（指 web/src），
 * 够不到仓库根的 src/。镜像 + 比对测试是这个仓库既有的做法（同 PET_SPECIES）。
 */
export const PET_QUALITY_MAX = 3;

/**
 * 长度上限，与服务端 checkPetBody 逐条对齐（由 pet.test.ts 比对列宽）。
 *
 * 前端不判长度的后果不是「这一行存不下」——是**整批保存在最后一个块炸掉**：
 * 保存按块顺序推，pets 排在最后，前面的关卡/卡包/拼图已经写进新草稿了，
 * 而 `curVid` 不前进，运营看到的是「保存失败」加一个孤儿草稿。
 */
export const ID_MAX = 64;
export const NAME_MAX = 64;
export const COLOR_MAX = 32;

/**
 * 一组 Spine 必须齐的三个后缀，各一个。**都必填**（leo 2026-09-07；`.meta` 09-08 去掉）。
 *
 * 判定按**后缀**而不是按数量：数量对但传了两个 `.png`、少一个 `.skel` 的话，
 * 客户端拿到的是一组加载不起来的资源，而且不报错。
 * 顺序就是界面上的展示顺序 —— 骨骼、图集、贴图。
 */
export const SPINE_EXTS = UPLOAD_NAMED_EXTS;
export const ANIM_ASSET_MAX = SPINE_EXTS.length;

/** 静态资源：1 个，**必填**，格式不限（leo 2026-09-04 / 09-07） */
export const STATIC_ASSET_MAX = 1;

/** 一份素材属于哪个后缀槽位。实现在 lib/upload.ts，与服务端同一份规则 */
export const slotOf = spineSlotOf;

/**
 * 这一组动画资源缺什么 / 多什么 / 是不是同一套导出。返回 null = 没问题。
 *
 * **这是硬闸，不是提示**（leo 2026-09-07）：填不齐就存不下。
 * 服务端 checkPetBody 末尾有同样一道，这里只是省掉一趟必然失败的保存。
 */
export function spineGroupIssue(
  assets: PetAsset[],
): { missing: string[]; dup: string[]; bases: string[] } | null {
  const { missing, dup } = spineGroupGaps(assets.map((a) => a.name));
  /*
   * 除了「三个后缀齐不齐」，还要判**这三个是不是同一套导出**。
   *
   * 只数后缀的话，`a.skel` + `b.atlas` + `c.png` 三个来自不同导出的文件会显示成
   * 绿色的 3/3，而客户端拿到的正是本函数存在的理由所说的那种「加载不起来且不报错」
   * 的组合。Spine 重导出换个名字是常事。
   */
  const bases = new Set<string>();
  for (const a of assets) {
    const slot = slotOf(a.name);
    if (slot) bases.add(a.name.slice(0, a.name.length - slot.length).toLowerCase());
  }
  const badBases = bases.size > 1 ? [...bases].sort() : [];
  return missing.length || dup.length || badBases.length
    ? { missing: [...missing], dup: [...dup], bases: badBases }
    : null;
}

/**
 * 把一份动画素材放进它的槽位，**替换**该槽位原有的那份。
 *
 * 🔴 去重的键是**后缀**，不是文件名。按文件名去重的话 `a.png` 和 `b.png` 是两个
 * 名字、却是同一个槽位，`.png` 那一格里会同时躺着两个文件（2026-09-08 实测）。
 * 约束写的是「每个后缀一个」，去重的键就必须是后缀。
 *
 * 替换也正是运营要的：重传一个 `.png` 就是**换素材**，旧的不该留在格子里等人手动删。
 *
 * @returns `replaced` = 被顶掉的**另一个**文件名（同名重传返回 null，那是明确的换这一个）
 */
export function putAnimAsset(
  assets: PetAsset[], item: PetAsset,
): { assets: PetAsset[]; replaced: string | null } {
  const slot = slotOf(item.name);
  // 后缀不认识的不收。accept 与上传校验各拦过一道，走到这儿说明有人绕过了
  if (!slot) return { assets, replaced: null };
  const gone = assets.find((x) => slotOf(x.name) === slot && x.name !== item.name);
  const next = [...assets.filter((x) => slotOf(x.name) !== slot), item];
  // 按槽位排序落库：不排的话数组顺序跟着上传顺序走，同一组素材因为传的先后不同
  // 会算出不同的块哈希，客户端白拉一次
  const rank = (n: string) => SPINE_EXTS.indexOf(slotOf(n) as (typeof SPINE_EXTS)[number]);
  return { assets: next.sort((x, y) => rank(x.name) - rank(y.name)), replaced: gone?.name ?? null };
}

/**
 * 这一行能不能存 / 有什么问题。返回的是 i18n key，页面自己翻译。
 *
 * 与服务端 checkPetBody 同一套口径 —— 前端先判是为了省掉一趟必然失败的保存，
 * **不是防线**：服务端仍然独立判一次。
 */
export function petIssues(p: Pet, all: Pet[]): MsgKey[] {
  const out: MsgKey[] = [];
  const id = p.petId.trim();
  if (!id) out.push("pet.err.noId");
  else if (id.length > ID_MAX) out.push("pet.err.longId");
  else if (!/^[A-Za-z0-9_-]+$/.test(id)) out.push("pet.err.badId");
  /*
   * 撞号**不分大小写**：`pet_id` 是 varchar 主键，MySQL 默认排序规则大小写不敏感，
   * 所以 `corgi_01` 和 `Corgi_01` 服务端是同一行。这里按大小写敏感比的话，
   * 页面放行、POST 才拿到 ER_DUP_ENTRY——而那时整批保存已经写了一半。
   */
  else if (all.some((x) => x.localKey !== p.localKey && x.petId.trim().toLowerCase() === id.toLowerCase())) {
    out.push("pet.err.dupId");
  }
  // en 必填：客户端缺当前语言时回退到它，没有 en 就是一个没名字的角色且不报错
  if (!p.nameI18n.en?.trim()) out.push("pet.err.noEn");
  // 长度上限与服务端 checkPetBody 逐条对齐（由 pet.test.ts 比对）。少一条的后果不是
  // 「这一行存不下」，而是**整批保存在最后一个块炸掉**——前面的关卡/卡包已经写进新草稿了
  else if (Object.values(p.nameI18n).some((v) => v.trim().length > NAME_MAX)) out.push("pet.err.longName");
  if (p.color.trim().length > COLOR_MAX) out.push("pet.err.longColor");
  // 只判 i18n：扁平值是它的派生值，服务端也从 en 重新派生（admin/pet.ts），
  // 判 Pet.species 的话新建行永远存不下——它到落库那一刻才被填上
  if (!speciesOf(p)) out.push("pet.err.noSpecies");
  else if (Object.values(p.speciesI18n).some((v) => v.trim().length > SPECIES_MAX)) out.push("pet.err.longSpecies");
  // 猫狗大类是发布闸的硬要求，也是写接口的必填项——空着这一行存不下
  if (!(PET_FAMILIES as readonly string[]).includes(p.family)) out.push("pet.err.badFamily");
  if (!(PET_SIZES as readonly string[]).includes(p.size)) out.push("pet.err.badSize");
  if (!Number.isInteger(p.quality) || p.quality < 1 || p.quality > PET_QUALITY_MAX) out.push("pet.err.badQuality");
  // 静态资源必填，缺了这一行**存不下**（服务端同一道，这里只是把话早点说出来）
  if (!p.staticAssets.length) out.push("pet.err.noStatic");
  /*
   * 动画资源 2026-09-16 起**非必填**：素材由美术分批交，逼着等齐三个文件才能建行
   * 等于让配置台卡在素材进度上。
   *
   * 但**要么不传、要么齐全**——半组比没有更糟：客户端按后缀挑文件交给 Spine，
   * 少一个就是加载不起来且不报错，表现为那只角色的展示区一片空白。
   * 所以下面两道只在「传了」的时候算，一个都没传时不拦（那是允许的中间态）。
   *
   * 「传了但不齐」会一直拦到补齐为止；「一个都没传」由 petWarnings 提醒，
   * 真正的兜底在发布闸（PET_ANIM_REQUIRED：启用的角色不能带着空动画上线）。
   */
  if (p.animAssets.length) {
    const g = spineGroupIssue(p.animAssets);
    if (g?.missing.length) out.push("pet.err.animMissing");
    if (g?.dup.length) out.push("pet.err.animDup");
  }
  return out;
}

/**
 * 不挡保存、但该让人看见的事。与 petIssues 分开：那个是「存不下的理由」，这个是「能存但上不了线」。
 *
 * 目前只有一条：**启用了却没配动画**。写接口放它过去，发布闸会拦
 * （PET_ANIM_REQUIRED）——不在列表上标出来的话，运营要到点发布那一刻才知道，
 * 而那正是当初把必填放在写接口的理由。
 */
export function petWarnings(p: Pet): Array<"pet.warn.animPending"> {
  const out: Array<"pet.warn.animPending"> = [];
  if (p.enabled && !p.animAssets.length) out.push("pet.warn.animPending");
  return out;
}

let localSeq = 0;
/** 新建一行草稿。**没有服务端身份**，保存时走 POST */
export function newPet(): Pet {
  localSeq += 1;
  return {
    localKey: `local_${localSeq}`,
    petId: "", persisted: false, published: null,
    name: "", nameI18n: {},
    // 品种 / 大类 / 体型都不给默认值：默认一个会让运营以为已经选过了，
    // 而这三个字段没有「多数情况都对」的取值（大类 cat 与 dog 各占一半）
    species: "", speciesI18n: {}, size: "", family: "", oshiIdolId: null,
    color: "", quality: 1, enabled: false,
    staticAssets: [], animAssets: [],
  };
}

/**
 * 服务端行 → 本地行。localKey 用 petId，已持久化的行 ID 不会变。
 *
 * ⚠️ **`published` 在读模型里是必填的，刻意不给可缺省的回落。**
 * 写成 `published?:` 再默认 `null` 的话，服务端 / 快照组装 / fixture 任意一处漏了这个字段
 * 都会**静默地把所有角色当成没发布过**——锁全开、删除按钮全亮，而类型检查一声不响。
 * 安全相关的标记不做兼容回落。
 */
export function toPet(r: PetRead): Pet {
  return { ...r, localKey: `srv_${r.petId}`, persisted: true };
}

/**
 * 本地行规范成服务端存的样子（多语言去空白、name / species 由 en 派生、本地键 srv_<编号>）。
 * 进 App 状态前都过一遍：协同合并与回包丢失对账靠「和服务端那一行一模一样」认领，
 * 本地多一个 local_N、少一个派生字段，同一只萌宠就会被认成两只
 */
export function normalizePet(p: Pet): Pet {
  const body = toServerPet(p);
  return { ...p, ...body, localKey: p.persisted ? p.localKey : `srv_${body.petId}` };
}

/** 本地行 → 落库形状。`name` 由 en 派生，不额外开一个输入框让人填两遍 */
export function toServerPet(p: Pet): PetBody {
  const nameI18n = Object.fromEntries(
    Object.entries(p.nameI18n).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
  );
  const speciesI18n = Object.fromEntries(
    Object.entries(p.speciesI18n).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
  );
  return {
    petId: p.petId.trim(),
    name: nameI18n.en ?? "",
    nameI18n,
    // 扁平 species 同 name：由 en 派生，不让人填两遍
    species: speciesI18n.en ?? "",
    speciesI18n,
    size: p.size,
    family: p.family,
    oshiIdolId: p.oshiIdolId?.trim() || null,
    color: p.color.trim(),
    quality: p.quality,
    enabled: p.enabled,
    staticAssets: p.staticAssets,
    animAssets: p.animAssets,
  };
}
